/**
 * Pure geometry and text for the overlays (owner B5). No MapLibre, no DOM: unit-tested.
 *
 *   ringFeatures(center, RING_KM)      geodesic distance rings + their label points at bearing 20° (§D.5)
 *   ringLabelMaxZoom(km)               the zoom at which a ring's label has faded (25 km z11, 15 km z12)
 *   futureLabelText(props)             "Inner Ring Road · Proposed" (upper-cased by the layer, §D.7)
 *   roadLabelText(props)               "Nagar Road · NH753F"
 *   metresPerPixel(lat, zoom)          512-px tiles: 74 143 / 2^z at the site
 *   smoothstep(e0, e1, x)              GLSL smoothstep (e0 > e1 allowed: a falling edge)
 */
import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import type { LngLat } from "@/contracts/data";
import { circle, destination } from "@/lib/geo";

export const RING_LABEL_BEARING = 20; // up and away from the left panel (§D.5)
const EARTH_CIRCUMFERENCE_M = 40075016.686;

export function metresPerPixel(lat: number, zoom: number): number {
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom);
}

export function smoothstep(e0: number, e1: number, x: number): number {
  if (e0 === e1) return x < e0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** 25 km labels leave below z11, 15 km below z12; 10 and 5 km stay until the rings fade (13 → 13.5). */
export function ringLabelMaxZoom(km: number): number {
  if (km >= 25) return 11;
  if (km >= 15) return 12;
  return 13.5;
}

export interface RingProps {
  km: number;
  ring: number; //  index 0 (inner) … n-1
}
export interface RingLabelProps extends RingProps {
  text: string;
  maxz: number;
}

/** Feature ids are numeric (ring index for lines, 100 + index for labels) so feature-state can address them. */
export const ringLabelId = (i: number): number => 100 + i;

export function ringFeatures(
  center: LngLat,
  kms: readonly number[],
  steps = 256,
): { lines: FeatureCollection<LineString, RingProps>; labels: FeatureCollection<Point, RingLabelProps> } {
  const lines: Feature<LineString, RingProps>[] = kms.map((km, i) => ({
    type: "Feature",
    id: i,
    properties: { km, ring: i },
    geometry: { type: "LineString", coordinates: circle(center, km * 1000, steps) },
  }));
  const labels: Feature<Point, RingLabelProps>[] = kms.map((km, i) => ({
    type: "Feature",
    id: ringLabelId(i),
    properties: { km, ring: i, text: `${km} KM`, maxz: ringLabelMaxZoom(km) },
    geometry: { type: "Point", coordinates: destination(center, km * 1000, RING_LABEL_BEARING) },
  }));
  return { lines: { type: "FeatureCollection", features: lines }, labels: { type: "FeatureCollection", features: labels } };
}

/* ─── Corridor text ──────────────────────────────────────────────────────── */

const STATUS_WORD: Record<string, string> = {
  proposed: "Proposed",
  "under-construction": "Under construction",
  indicative: "Indicative",
  existing: "",
};

const sentence = (s: string): string => {
  const t = s.trim().replace(/\s+/g, " ");
  return t ? t[0].toUpperCase() + t.slice(1).toLowerCase() : t;
};

/**
 * "{NAME} · {STATUS}" (§D.7). A plan status written into the name ("Inner Ring Road (proposed)") is the
 * source's own word for the road, so it wins over the alignment status ("indicative"), which the card
 * explains. The layer upper-cases the result.
 */
export function futureLabelText(p: { name: string; status: string }): string {
  const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(p.name);
  const base = (m ? m[1] : p.name).trim();
  const word = m ? sentence(m[2]) : (STATUS_WORD[p.status] ?? sentence(p.status.replace(/-/g, " ")));
  return word ? `${base} · ${word}` : base;
}

/** "Nagar Road · NH753F": the city prefix is dropped (the whole map is around Pune), the ref kept. */
export function roadLabelText(p: { name: string; ref?: string }): string {
  const name = p.name.replace(/^Pune\s*[–-]\s*/u, "").trim();
  return p.ref ? `${name} · ${p.ref}` : name;
}
